import { createBrowserRouter } from "react-router-dom";
import { LayoutWrapper } from "./LayoutWrapper";
import {
  protectedLoader,
  publicOnlyLoader,
  systemAdminLoader,
  tenantUserLoader,
  dashboardLoader,
  scopeProtectedLoader,
} from "./guards";
import { RouteErrorFallback } from "./RouteErrorFallback";
import NotFound from "@/pages/NotFound";

// --------------------------------------
// ✅ Completed PATHS object
// --------------------------------------
export const PATHS = {
  AUTH: "/auth",
  FORGOT_PASSWORD: "/forgot-password",
  RESET_PASSWORD: "/reset-password",
  ACCEPT_INVITATION: "/accept-invitation",
  EMAIL_VERIFICATION: "/email-verification",
  MAGIC_LINK: "/magic-link/:code",

  DASHBOARD: "/",
  TECHNICIAN_DASHBOARD: "technician-dashboard",

  ASSETS: "assets",
  /** Create asset; optional query ?modelId= locks the asset model */
  ASSET_CREATE: "assets/create",
  ASSET_DETAILS: (id: string) => `assets/${id}`,
  ASSET_LABEL: (id: string) => `assets/${id}/label`,
  ASSET_CATEGORIES: "assets/categories",
  ASSET_MODELS: "assets/models",
  ASSET_MODEL_DETAILS: (id: string) => `assets/models/${id}`,

  MAINTENANCE: "maintenance",
  MAINTENANCE_DETAILS: (id: string) => `maintenance/${id}`,

  INSPECTIONS: "inspections",
  INSPECTION_DETAILS: (id: string) => `inspections/${id}`,

  PARTS: "parts",
  PART_DETAILS: (id: string) => `parts/${id}`,
  PART_LABEL: (id: string) => `parts/${id}/label`,

  BRANCHES: "branches",
  BRANCH_DETAILS: (id: string) => `branches/${id}`,
  BRANCH_LABEL: "branches/:id/label",
  BRANCH_CATEGORIES: "branches/categories",

  WAREHOUSES: "warehouses",

  SUPPLIERS: "suppliers",
  SUPPLIER_DETAILS: (id: string) => `suppliers/${id}`,

  EVENTS: "events",
  EVENT_DETAILS: (id: string) => `events/${id}`,
  ANALYSIS: "analysis",

  TICKETS: "tickets",
  TICKET_NEW: "tickets/new",
  TICKET_DETAILS: (id: string) => `tickets/${id}`,
  ISSUE_CATEGORIES: "tickets/categories",
  TICKET_QUEUES: "tickets/queues",
  DRAFT_TICKETS: "tickets/drafts",
  DRAFT_TICKET_DETAILS: (id: string) => `tickets/drafts/${id}`,

  PEOPLE: "people",
  USERS: "users",
  POLICIES: "policies",
  ACCOUNTS: "accounts",
  ACCOUNT_DETAILS: (id: string) => `accounts/${id}`,

  SETTINGS: "settings",
};

export const router = createBrowserRouter([
  // ── Auth routes (no layout, public-only) ─────────────────────────────────
  {
    path: PATHS.AUTH,
    loader: publicOnlyLoader,
    lazy: () => import("@features/auth/pages/LoginPage").then((m) => ({ Component: m.LoginPage })),
  },
  {
    path: PATHS.FORGOT_PASSWORD,
    loader: publicOnlyLoader,
    lazy: () =>
      import("@features/auth/pages/ForgotPasswordPage").then((m) => ({
        Component: m.ForgotPasswordPage,
      })),
  },
  {
    path: PATHS.RESET_PASSWORD,
    loader: publicOnlyLoader,
    lazy: () =>
      import("@features/auth/pages/ResetPasswordPage").then((m) => ({
        Component: m.ResetPasswordPage,
      })),
  },
  {
    path: PATHS.ACCEPT_INVITATION,
    loader: publicOnlyLoader,
    lazy: () =>
      import("@features/invitation/pages/AcceptInvitationPage").then((m) => ({
        Component: m.AcceptInvitationPage,
      })),
  },
  {
    path: PATHS.EMAIL_VERIFICATION,
    loader: publicOnlyLoader,
    lazy: () =>
      import("@features/invitation/pages/EmailVerificationPage").then((m) => ({
        Component: m.EmailVerificationPage,
      })),
  },
  {
    path: PATHS.MAGIC_LINK,
    loader: publicOnlyLoader,
    lazy: () =>
      import("@features/auth/pages/MagicLinkPage").then((m) => ({ Component: m.MagicLinkPage })),
  },

  // ── Protected routes (with layout) ───────────────────────────────────────
  {
    path: "/",
    loader: protectedLoader,
    element: <LayoutWrapper />,
    errorElement: <RouteErrorFallback />,
    children: [
      // Dashboard (index)
      {
        index: true,
        loader: dashboardLoader,
        lazy: () =>
          import("@features/dashboard/pages/DashboardPage").then((m) => ({ Component: m.default })),
      },

      // System admin routes
      {
        path: PATHS.ACCOUNTS,
        loader: systemAdminLoader,
        lazy: () =>
          import("@/features/account/pages/AccountManagementPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: PATHS.ACCOUNT_DETAILS(":id"),
        loader: systemAdminLoader,
        lazy: () =>
          import("@/features/account/pages/AccountDetailsPage").then((m) => ({
            Component: m.default,
          })),
      },

      // Tenant user routes
      {
        path: PATHS.TECHNICIAN_DASHBOARD,
        loader: tenantUserLoader,
        lazy: () =>
          import("@/features/technician/pages/TechnicianDashboardPage").then((m) => ({
            Component: m.default,
          })),
      },

      // Scope-protected routes
      {
        path: PATHS.ASSETS,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/asset/pages/AssetManagementPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: PATHS.ASSET_CREATE,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/asset/pages/AssetCreatePage").then((m) => ({ Component: m.default })),
      },
      {
        path: PATHS.ASSET_DETAILS(":id"),
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/asset/pages/AssetDetailsPage").then((m) => ({ Component: m.default })),
      },
      {
        path: PATHS.ASSET_LABEL(":id"),
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/asset/pages/AssetLabelPage").then((m) => ({ Component: m.default })),
      },
      {
        path: PATHS.ASSET_CATEGORIES,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/asset/pages/AssetCategoriesPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: PATHS.ASSET_MODELS,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/asset/pages/AssetModelsPage").then((m) => ({ Component: m.default })),
      },
      {
        path: PATHS.ASSET_MODEL_DETAILS(":id"),
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/asset/pages/AssetModelDetailsPage").then((m) => ({
            Component: m.default,
          })),
      },

      {
        path: PATHS.MAINTENANCE,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/maintenance/pages/MaintenancePage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: PATHS.MAINTENANCE_DETAILS(":id"),
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/maintenance/pages/MaintenanceDetails").then((m) => ({
            Component: m.default,
          })),
      },

      {
        path: PATHS.INSPECTIONS,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/inspection/pages/InspectionsPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: PATHS.INSPECTION_DETAILS(":id"),
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/inspection/pages/InspectionDetails").then((m) => ({
            Component: m.default,
          })),
      },

      {
        path: PATHS.PARTS,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/part/pages/PartsManagementPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: PATHS.PART_DETAILS(":id"),
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/part/pages/PartDetailsPage").then((m) => ({ Component: m.default })),
      },
      {
        path: PATHS.PART_LABEL(":id"),
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/part/pages/PartLabelPage").then((m) => ({ Component: m.default })),
      },

      {
        path: PATHS.WAREHOUSES,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/warehouse/pages/WarehouseManagementPage").then((m) => ({
            Component: m.default,
          })),
      },

      {
        path: PATHS.BRANCHES,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/branch/pages/BranchManagementPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: PATHS.BRANCH_DETAILS(":id"),
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/branch/pages/BranchDetailsPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: PATHS.BRANCH_LABEL,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/branch/pages/BranchLabelPage").then((m) => ({ Component: m.default })),
      },
      {
        path: PATHS.BRANCH_CATEGORIES,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/branch/pages/BranchCategoriesPage").then((m) => ({
            Component: m.default,
          })),
      },

      {
        path: PATHS.SUPPLIERS,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/supplier/pages/SupplierManagementPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: PATHS.SUPPLIER_DETAILS(":id"),
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/supplier/pages/SupplierDetailsPage").then((m) => ({
            Component: m.default,
          })),
      },

      {
        path: PATHS.TICKETS,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/ticket/pages/TicketsPage").then((m) => ({ Component: m.default })),
      },
      {
        path: PATHS.TICKET_NEW,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/ticket/pages/TicketNewPage").then((m) => ({ Component: m.default })),
      },
      {
        path: PATHS.TICKET_DETAILS(":id"),
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/ticket/pages/TicketDetailsPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: PATHS.ISSUE_CATEGORIES,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/ticket/pages/IssueCategoriesPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: PATHS.TICKET_QUEUES,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/ticket/pages/TicketQueuesPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: PATHS.DRAFT_TICKET_DETAILS(":id"),
        loader: scopeProtectedLoader,
        lazy: () => import("@/pages/DraftTicketDetailPage").then((m) => ({ Component: m.default })),
      },

      {
        path: PATHS.EVENTS,
        loader: scopeProtectedLoader,
        lazy: () => import("@/features/event/pages/Events").then((m) => ({ Component: m.default })),
      },
      {
        path: PATHS.EVENT_DETAILS(":id"),
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/event/pages/EventDetailsPage").then((m) => ({ Component: m.default })),
      },
      {
        path: PATHS.ANALYSIS,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/analysis/pages/AnalysisPage").then((m) => ({ Component: m.default })),
      },

      {
        path: PATHS.PEOPLE,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/person/pages/PeoplePage").then((m) => ({ Component: m.default })),
      },
      {
        path: PATHS.USERS,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/user/pages/UserManagementPage").then((m) => ({
            Component: m.UserManagementPage,
          })),
      },
      {
        path: PATHS.POLICIES,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/policy/pages/PoliciesPage").then((m) => ({ Component: m.default })),
      },
      {
        path: PATHS.SETTINGS,
        loader: scopeProtectedLoader,
        lazy: () =>
          import("@/features/settings/pages/Settings").then((m) => ({ Component: m.default })),
      },

      { path: "*", element: <NotFound /> },
    ],
  },

  // Global catch-all
  { path: "*", element: <NotFound /> },
]);
